import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ArrowLeft, Loader2, Send, X } from 'lucide-react';

import { searchPeople, startChat } from '../../services/teamsDmApi';

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const MAX_PEOPLE = 20;

/**
 * Pick people (or type emails) and write the first message. One person is a
 * 1:1 (Teams reuses an existing one); two or more start a group chat, which
 * can be named.
 */
const NewChat = ({ accessToken, onBack, onStarted }) => {
  const [query, setQuery] = useState('');
  const [people, setPeople] = useState([]);
  const [to, setTo] = useState([]);
  const [topic, setTopic] = useState('');
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (query.trim().length < 2) {
      setPeople([]);
      return undefined;
    }
    const timer = setTimeout(() => {
      searchPeople(accessToken, query.trim())
        .then((res) =>
          setPeople(
            (res?.data || []).filter(
              (p) => !to.some((t) => t.email === p.email),
            ),
          ),
        )
        .catch(() => setPeople([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [accessToken, query, to]);

  const add = (person) => {
    if (to.length >= MAX_PEOPLE || to.some((t) => t.email === person.email))
      return;
    setTo((list) => [...list, person]);
    setQuery('');
    setPeople([]);
  };

  // Several people, or one with a name, is a group (as in Teams).
  const group = to.length > 1 || Boolean(topic.trim());
  const value = text.trim();
  const send = async () => {
    if (!to.length || !value || sending) return;
    setSending(true);
    try {
      const res = await startChat(
        accessToken,
        to.map((t) => t.email),
        value,
        group ? topic.trim() : null,
      );
      toast.success('Sent');
      onStarted(
        res?.data?.chat_id,
        group ? topic.trim() || to.map((t) => t.name).join(', ') : to[0].name,
        group ? 'group' : 'direct',
      );
    } catch (err) {
      toast.error(`Could not start the chat: ${err?.message || 'try again'}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 items-center gap-2 border-b border-zinc-800 px-3 py-2">
        <button
          type="button"
          aria-label="Back to chats"
          onClick={onBack}
          className="rounded-md p-1 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <span className="text-[13px] font-medium text-zinc-100">
          {group ? 'New group chat' : 'New chat'}
        </span>
      </div>
      <div className="flex flex-col gap-2 p-3">
        <div className="relative">
          <div className="flex flex-wrap items-center gap-1 rounded-lg border border-zinc-700 bg-zinc-900 px-2 py-1.5">
            {to.map((person) => (
              <span
                key={person.email}
                title={person.email}
                className="flex items-center gap-1 rounded bg-zinc-700/60 px-1.5 py-0.5 text-[12px] text-zinc-100"
              >
                {person.name}
                <button
                  type="button"
                  aria-label={`Remove ${person.name}`}
                  onClick={() =>
                    setTo((list) =>
                      list.filter((t) => t.email !== person.email),
                    )
                  }
                  className="text-zinc-400 hover:text-zinc-100"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
            <input
              aria-label="To"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                const typed = query.trim();
                if (e.key === 'Enter' && EMAIL.test(typed)) {
                  e.preventDefault();
                  add({ name: typed, email: typed });
                }
                if (e.key === 'Backspace' && !query && to.length) {
                  setTo((list) => list.slice(0, -1));
                }
              }}
              placeholder={to.length ? 'Add someone' : 'Name or email'}
              className="min-w-[100px] flex-1 bg-transparent text-[13px] text-zinc-100 outline-none"
            />
          </div>
          {people.length > 0 && (
            <ul className="absolute z-10 mt-1 w-full overflow-hidden rounded-lg border border-zinc-700 bg-zinc-900 shadow-lg">
              {people.map((person) => (
                <li key={person.email}>
                  <button
                    type="button"
                    onClick={() => add(person)}
                    className="flex w-full flex-col px-2 py-1.5 text-left hover:bg-zinc-800"
                  >
                    <span className="text-[13px] text-zinc-100">
                      {person.name}
                    </span>
                    <span className="text-[11px] text-zinc-500">
                      {person.email}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        {to.length > 0 && (
          <input
            aria-label="Group name"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            maxLength={250}
            placeholder={
              to.length > 1
                ? 'Group name (optional)'
                : 'Group name (makes it a group chat)'
            }
            className="w-full rounded-lg border border-zinc-700 bg-zinc-900 px-2 py-1.5 text-[13px] text-zinc-100 outline-none focus:border-[#5652CF]"
          />
        )}
        <textarea
          aria-label="First message"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          placeholder="Write a message"
          className="w-full rounded-lg border border-zinc-700 bg-zinc-900 p-2 text-[13px] text-zinc-100 outline-none focus:border-[#5652CF]"
        />
        <div className="flex justify-end">
          <button
            type="button"
            onClick={send}
            disabled={!to.length || !value || sending}
            className="flex items-center gap-1.5 rounded-lg bg-[#5652CF] px-3 py-1.5 text-[12px] font-semibold text-white hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {sending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Send className="h-3.5 w-3.5" />
            )}
            Send
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewChat;
